/**
 * Consent Management Module
 * Handles GDPR/Privacy compliance based on user's geographic location
 */

import Alpine from 'alpinejs';
import { isProduction } from '../utils/environment';

// List of countries requiring strict prior consent (EU/EEA/UK/CH)
const STRICT_CONSENT_COUNTRIES = [
  "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE",
  "GR","HU","IE","IT","LV","LT","LU","MT","NL","PL","PT",
  "RO","SK","SI","ES","SE", // EU
  "IS","LI","NO",            // EEA
  "GB",                      // UK
  "CH"                       // Switzerland
];

export interface ConsentStatus {
  status: string | ConsentPreferences;
  timestamp: string;
  country: string;
}

export interface ConsentPreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

declare global {
  interface Window {
    USER_COUNTRY?: string;
  }
}

class ConsentManager {
  private static instance: ConsentManager;

  private constructor() {}

  static getInstance(): ConsentManager {
    if (!ConsentManager.instance) {
      ConsentManager.instance = new ConsentManager();
    }
    return ConsentManager.instance;
  }

  /**
   * Check if strict consent is required based on user's country
   */
  requiresStrictConsent(): boolean {
    const country = window.USER_COUNTRY;
    if (!country) return true; // fail-safe: require consent if unknown
    return STRICT_CONSENT_COUNTRIES.includes(country);
  }

  /**
   * Get consent status from localStorage
   */
  getConsentStatus(): ConsentStatus | null {
    try {
      const consent = localStorage.getItem('lunai_consent');
      return consent ? JSON.parse(consent) : null;
    } catch (e) {
      console.error('Error reading consent status:', e);
      return null;
    }
  }

  /**
   * Save consent status to localStorage
   */
  saveConsentStatus(status: string | ConsentPreferences): void {
    try {
      const consentData: ConsentStatus = {
        status,
        timestamp: new Date().toISOString(),
        country: window.USER_COUNTRY || 'unknown'
      };
      localStorage.setItem('lunai_consent', JSON.stringify(consentData));

      // Dispatch event for other modules
      window.dispatchEvent(new CustomEvent('consent-granted', {
        detail: typeof status === 'string'
          ? this.parseStringStatus(status)
          : status
      }));
    } catch (e) {
      console.error('Error saving consent status:', e);
    }
  }

  private parseStringStatus(status: string): ConsentPreferences {
    switch (status) {
      case 'accepted':
      case 'accepted-implied':
        return { essential: true, analytics: true, marketing: true };
      case 'essential-only':
        return { essential: true, analytics: false, marketing: false };
      default:
        return { essential: true, analytics: false, marketing: false };
    }
  }

  /**
   * Check if user has granted specific consent type
   */
  hasConsent(type: 'analytics' | 'marketing'): boolean {
    const status = this.getConsentStatus();
    if (!status) return false;

    if (typeof status.status === 'string') {
      const prefs = this.parseStringStatus(status.status);
      return prefs[type];
    }

    return (status.status as ConsentPreferences)[type] || false;
  }

  /**
   * Initialize consent system
   */
  async initialize(): Promise<void> {
    // Dev/test convenience: ?geo=XX overrides the detected country (never in production)
    this.applyGeoOverride();

    // Fast path: a stored decision needs no geo. Returning visitors — including
    // every implied-consent (e.g. US) visitor after their first visit — get
    // their consent re-dispatched immediately, without waiting for geo.js.
    // Note: this can run before waitForConsent() registers its listener;
    // utils/consent.ts covers that with a synchronous stored-status check.
    const status = this.getConsentStatus();
    if (status && status.status !== 'pending') {
      const prefs = typeof status.status === 'string'
        ? this.parseStringStatus(status.status)
        : status.status as ConsentPreferences;

      window.dispatchEvent(new CustomEvent('consent-granted', { detail: prefs }));
      return;
    }

    // First visit: wait for geo detection to classify the region
    await this.waitForGeoDetection();

    // Get Alpine store
    const store = Alpine.store('consent') as any;

    // Check if consent is required
    if (!this.requiresStrictConsent()) {
      // For non-strict countries, assume consent
      this.saveConsentStatus('accepted-implied');
      return;
    }

    // Show consent banner
    store.showBanner = true;
    store.requiresConsent = true;
    store.country = window.USER_COUNTRY;

    // If the banner is up only because geo hadn't resolved yet (fail-safe),
    // keep watching for a late answer and self-correct for non-strict visitors.
    this.watchForLateGeo(store);
  }

  /**
   * The async geo.js can resolve after the 3s consent poll. If the banner was
   * shown as the unknown-country fail-safe, watch for the country to arrive:
   * a non-strict (e.g. US) visitor who hasn't clicked yet gets implied consent
   * and the banner is hidden — restoring the "no banner outside strict regions"
   * behavior instead of punishing slow geo responses for the whole session.
   */
  private watchForLateGeo(store: any): void {
    if (window.USER_COUNTRY !== undefined) return; // real strict-country banner
    const deadline = Date.now() + 60000;
    const timer = setInterval(() => {
      if (window.USER_COUNTRY === undefined) {
        if (Date.now() > deadline) clearInterval(timer);
        return;
      }
      clearInterval(timer);
      const status = this.getConsentStatus();
      if (status && status.status !== 'pending') return; // user already chose
      store.country = window.USER_COUNTRY;
      if (!this.requiresStrictConsent()) {
        store.showBanner = false;
        store.requiresConsent = false;
        this.saveConsentStatus('accepted-implied');
      }
    }, 250);
  }

  /**
   * Non-production only: allow ?geo=XX to override the detected country so the
   * strict-consent (EU) and implied-consent paths are testable locally/on sandbox.
   */
  private applyGeoOverride(): void {
    if (isProduction()) return;
    const override = new URLSearchParams(window.location.search).get('geo');
    if (override) {
      // Non-writable so the late-arriving async geo.js (a plain
      // `window.USER_COUNTRY = "XX"` assignment) can't clobber the override.
      Object.defineProperty(window, 'USER_COUNTRY', {
        value: override.toUpperCase(),
        writable: false,
        configurable: true
      });
      console.log(`Consent: using geo override from URL: ${window.USER_COUNTRY}`);
    }
  }

  private async waitForGeoDetection(): Promise<void> {
    // Wait up to 3 seconds for geo detection
    const timeout = 3000;
    const startTime = Date.now();

    while (window.USER_COUNTRY === undefined && Date.now() - startTime < timeout) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    if (window.USER_COUNTRY === undefined) {
      console.warn('Geo detection timeout - assuming consent required');
    }
  }

  /**
   * Revoke consent (for privacy settings page)
   */
  revokeConsent(): void {
    localStorage.removeItem('lunai_consent');
    window.location.reload();
  }
}

// Export singleton instance
export const consentManager = ConsentManager.getInstance();

/**
 * Programmatically open the consent settings modal (e.g. from a privacy page link)
 */
export function showConsentSettings(): void {
  const store = Alpine.store('consent') as any;
  if (store) store.showSettings = true;
}

// Initialize Alpine data for consent
export function initConsentAlpineData(): void {
  Alpine.store('consent', {
    // State
    showBanner: false,
    showSettings: false,
    preferences: {
      essential: true,
      // Settings-modal toggles default ON (product decision: the easiest
      // click should be all-on; "Essential Only" is the explicit opt-out).
      analytics: true,
      marketing: true
    },
    requiresConsent: false,
    country: undefined,

    // Methods
    acceptAll() {
      consentManager.saveConsentStatus('accepted');
      this.showBanner = false;
    },

    savePreferences() {
      consentManager.saveConsentStatus(this.preferences);
      this.showBanner = false;
      this.showSettings = false;
    }
  });

  // Initialize consent banner from template
  initConsentBanner();

  // Initialize consent manager after store is ready
  consentManager.initialize();
}

/**
 * Initialize consent banner by cloning from template
 */
function initConsentBanner(): void {
  const template = document.getElementById('consent-banner-template') as HTMLTemplateElement;
  if (!template) {
    console.error('Consent banner template not found');
    return;
  }

  const clone = template.content.cloneNode(true);
  document.body.appendChild(clone);
}